import React from "react";

import DevAccountSwitcher from "@/contexts/DevAccountSwitcher";
import DevAuthSwitcher from "@/contexts/DevAuthSwitcher";
import DevUserSwitcher from "@/contexts/DevUserSwitcher";
import { useAppSelector } from "@/store/hooks";

/* NODE_ENV is "production" in the dev *build* too, so keying off it hides the
   rail on the deployed dev site (dev.audiosanctum.com) — only localhost saw it.
   Key off the hostname instead (same pattern as DevModalLauncher): visible on
   localhost + any dev.* host, never on production. */
const isDevHost = (() => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h.startsWith("dev.") || h.includes(".dev.");
})();

/**
 * The dev switchers ship with a canonical test account so QA can flip
 * subscription / claim / banner states quickly. We don't want them to
 * appear for any other (real or seeded) user logged in on the dev box —
 * the switchers issue API calls scoped to whoever is logged in, so
 * exposing them to other accounts would mutate their data unexpectedly.
 */
const DEV_RAIL_OWNER_EMAIL = "hoyaw23479@seosnaps.com";

/**
 * Dev-only fixed rail anchored to the left edge of the viewport, sitting
 * just below the site header + LoggedInBanner. Stacks the dev switchers
 * vertically so each one stays readable regardless of browser width.
 *
 * - Header (~72px) + LoggedInBanner (~56px) = ~128px of fixed top chrome
 *   when logged in. We add ~16px of breathing space → top: 144px.
 * - When logged out the banner is hidden, but the extra 56px of vertical
 *   space below the header is still fine — the rail just sits a bit
 *   lower than strictly needed.
 */
const DevSwitcherRail: React.FC = () => {
  const userEmail = useAppSelector((state) => state.user.user?.email);
  if (!isDevHost) return null;
  if (userEmail !== DEV_RAIL_OWNER_EMAIL) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: 184,
        left: 12,
        zIndex: 90,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 8,
        pointerEvents: "auto",
      }}
    >
      <DevAccountSwitcher />
      <DevUserSwitcher />
      <DevAuthSwitcher />
    </div>
  );
};

export default DevSwitcherRail;
